import { useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import { Box } from '@mui/material';
import TeamTagsAutocomplete from 'components/TeamTagsAutocomplete';
import useTeams from 'hooks/useTeams';
import { useAppDispatch } from 'hooks';
import { updateTeam } from 'thunk';
import { UpdateTeamParams } from 'ProfileModule/types';

type UpdateTeamTagsParams = UpdateTeamParams & {
  team_id: number;
  tags?: string[];
  clear_tags?: boolean;
  onSuccess?: () => void;
};

export type EditTagsModalProps = { teamId: number; onClose: VoidFunction };

const EditTagsModal: React.FC<EditTagsModalProps> = ({ teamId, onClose }) => {
  const dispatch = useAppDispatch();
  const { teamHash, fetchTeams } = useTeams({ shouldAutoLoad: false });
  const team = teamHash[teamId];
  const [tags, setTags] = useState<string[]>(
    Array.isArray(team?.tags) ? (team!.tags as string[]) : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={() => {
        setSubmitting(true);
        setError('');
        const payload: UpdateTeamTagsParams = {
          team_id: teamId,
          ...(tags.length ? { tags } : { clear_tags: true }),
          onSuccess: () => {
            fetchTeams({ teamIds: [teamId] });
            setSubmitting(false);
            onClose();
          },
          onFailure: ({ error }: any) => {
            setSubmitting(false);
            setError((error as any)?.message || 'Failed to update tags');
          },
        } as UpdateTeamTagsParams;
        // Wait for update to succeed before refetching team data to avoid stale UI
        dispatch(updateTeam(payload));
      }}
      headerText={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Edit Tags</span>
          <span style={{ cursor: 'pointer' }} onClick={onClose}>
            ×
          </span>
        </Box>
      }
      confirmButtonText="Update"
      disableConfirmButton={submitting}
      Body={
        <Box>
          <TeamTagsAutocomplete value={tags} onChange={setTags} label="Tags" />
          {error ? (
            <Box
              sx={{
                mt: 2,
                color: 'red',
              }}
            >
              {error}
            </Box>
          ) : null}
        </Box>
      }
    />
  );
};

export default EditTagsModal;
export { EditTagsModal };
